import { RefObject } from 'react'

import { isQuestionUnanswered } from '../../../actions/defectAssistant'
import { AnswerTypes, OptionKinds, OptionValue, QnA } from '../../../actions/defectAssistant/types'
import {
  ActiveSingleSelect,
  FutureSection,
  LockedDuplicateCheck,
  LockedQuestion,
  ScrollDuplicateCheck,
  ScrollMultiSelect,
} from './questionSections'

type QuestionSectionProps = {
  qna: QnA
  qnaIdx: number
  /** Index of the multi-select being re-edited; sections after it are hidden. */
  reEditingIdx: number | null
  currentQuestion: QnA | undefined
  lastAnsweredIdx: number
  questionLoading: boolean
  amendLoading: boolean
  showCommentFieldInitially: boolean
  suggestedComment: string | null
  suggestedCommentLoading: boolean
  /** Ref for the section the flow should scroll to. */
  activeRef: RefObject<HTMLDivElement>
  onSubmit: (answers: OptionValue[]) => void
  onReEditSubmit: (answers: OptionValue[]) => void
  onReOpen: () => void
  onReEdit: () => void
  onReOpenForAmend: () => void
  onAmendComment: (defectId: string, comment: string) => void
  onAbortRequest: () => void
}

/** One question of the assistant flow, rendered in whichever state it is in: locked, active, future
 * or being re-edited. */
export const QuestionSection = ({
  qna,
  qnaIdx,
  reEditingIdx,
  currentQuestion,
  lastAnsweredIdx,
  questionLoading,
  amendLoading,
  showCommentFieldInitially,
  suggestedComment,
  suggestedCommentLoading,
  activeRef,
  onSubmit,
  onReEditSubmit,
  onReOpen,
  onReEdit,
  onReOpenForAmend,
  onAmendComment,
  onAbortRequest,
}: QuestionSectionProps): JSX.Element | null => {
  if (reEditingIdx !== null && qnaIdx > reEditingIdx) return null

  if (reEditingIdx !== null && qnaIdx === reEditingIdx) {
    return (
      <div ref={activeRef}>
        <ScrollMultiSelect question={qna} loading={questionLoading} onSubmit={onReEditSubmit} />
      </div>
    )
  }

  const answered = !isQuestionUnanswered(qna)
  const isActive = currentQuestion?.uuid === qna.uuid
  const isMulti = qna.answerType === AnswerTypes.MULTISELECT

  if (isMulti && answered && questionLoading && !currentQuestion && qnaIdx === lastAnsweredIdx) {
    return (
      <div ref={activeRef}>
        <ScrollMultiSelect question={qna} loading onSubmit={onSubmit} />
      </div>
    )
  }

  if (!answered && !isActive) return <FutureSection qna={qna} />

  if (answered) {
    if (qna.matchedDefect) {
      return (
        <LockedDuplicateCheck
          qna={qna}
          onReOpen={(opt) => {
            onReOpen()
            if (opt.kind === OptionKinds.ADD_COMMENT_TO_EXISTING_DEFECT) onReOpenForAmend()
          }}
          onAbort={onAbortRequest}
        />
      )
    }
    return <LockedQuestion qna={qna} onReOpen={isMulti ? onReEdit : onReOpen} />
  }

  if (qna.matchedDefect) {
    return (
      <div ref={activeRef}>
        <ScrollDuplicateCheck
          question={qna}
          loading={questionLoading}
          amendLoading={amendLoading}
          showCommentFieldInitially={showCommentFieldInitially}
          suggestedComment={suggestedComment}
          suggestedCommentLoading={suggestedCommentLoading}
          onSubmit={onSubmit}
          onAmendComment={onAmendComment}
          onAbortRequest={onAbortRequest}
        />
      </div>
    )
  }
  if (isMulti) {
    return (
      <div ref={activeRef}>
        <ScrollMultiSelect question={qna} loading={questionLoading} onSubmit={onSubmit} />
      </div>
    )
  }
  return (
    <div ref={activeRef}>
      <ActiveSingleSelect qna={qna} loading={questionLoading} onPick={(opt) => onSubmit([opt])} />
    </div>
  )
}
